<script setup lang="ts">
import type { VerticalNavigationLink } from '#ui/types'
import { GetRepositoriesDocument } from './Repositories.generated'

const props = defineProps<{
  repositoryGid: string
}>()

const { enabled: areSleuthSkillsEnabled } = useFlag('SLEUTH_SKILLS')

const route = useRoute()

const { data: repositoriesData } = useQuery({
  query: GetRepositoriesDocument,
  paused: computed(() => !areSleuthSkillsEnabled.value),
})

const repository = computed(() => {
  const repos = repositoriesData.value?.organization?.skillsRepositories?.edges?.map(e => e.node) ?? []
  return repos.find(r => r.gid === props.repositoryGid)
})

const onRoute = (routeNames: string[]) => routeNames.includes(route.name as string)

function getSubLinks() {
  const links: (VerticalNavigationLink & { showConfigWarning?: boolean, iconClass?: string })[] = []

  if (areSleuthSkillsEnabled.value) {
    links.push({
      label: 'Suggested Skills',
      to: `/repositories/${props.repositoryGid}/suggested-skills`,
      icon: 'solid:sparkles',
      iconClass: 'grayscale',
      active: onRoute([
        'repositories-gid-suggested-skills',
      ]),
    })
    links.push({
      label: 'Skills',
      to: `/repositories/${props.repositoryGid}/skills`,
      icon: 'solid:book',
      active: onRoute([
        'repositories-gid-skills',
        'repositories-gid-skills-hash',
      ]),
    })
  }

  return links
}

function onRepositoryHome() {
  return route.name === 'repositories-gid'
}
</script>

<template>
  <div v-if="repository" class="flex flex-col px-2">
    <UButton
      color="white-nav"
      variant="ghost"
      active-class="dark:bg-gray-800 bg-gray-200"
      class="group/navItem relative mb-px flex flex-1 justify-between px-2"
      :class="onRepositoryHome() ? ['bg-blue-80', 'dark:bg-gray-900'] : []"
      @click="navigateTo(`/repositories/${repositoryGid}`)"
    >
      <div v-if="onRepositoryHome()" class="absolute inset-y-1.5 left-[-5px] w-[3px] rounded-md bg-blue-600" />
      <UTooltip
        :text="`${repository.repository.owner}/${repository.repository.name}`"
        :prevent="`${repository.repository.owner}/${repository.repository.name}`.length < 23"
        :popper="{ placement: 'top', offsetDistance: 14 }"
        :open-delay="500"
        class="flex flex-1 items-center gap-x-2 truncate"
      >
        <UIcon name="i-simple-icons-github" class="size-4 shrink-0 grow-0 text-gray-500 dark:text-gray-400" />
        <span class="truncate text-left text-nav font-light text-gray-900 dark:text-white">
          {{ repository.repository.owner }}/{{ repository.repository.name }}
        </span>
      </UTooltip>
    </UButton>

    <div class="flex flex-col">
      <UVerticalNavigation
        :links="getSubLinks()"
        class="pl-4"
        :ui="{
          base: 'group relative flex items-center gap-1.5 focus:outline-none focus-visible:outline-none dark:focus-visible:outline-none focus-visible:before:ring-inset focus-visible:before:ring-1 focus-visible:before:ring-primary-500 dark:focus-visible:before:ring-primary-400 before:absolute before:inset-px before:rounded-md disabled:cursor-not-allowed disabled:opacity-75',
          ring: '',
          padding: 'py-2 px-2',
          width: 'w-full',
          rounded: 'rounded-md',
          font: 'font-light',
          size: 'text-nav',
          active: 'text-gray-900 dark:text-white before:bg-gray-100 dark:before:bg-gray-800',
          inactive: 'text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:before:bg-gray-50 dark:hover:before:bg-gray-800/50',
          label: 'truncate relative',
          icon: {
            base: 'flex-shrink-0 w-4 h-4',
            active: 'text-gray-700 dark:text-gray-200',
            inactive: 'text-gray-400 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-200',
          },
        }"
      >
        <template #icon="{ link, isActive }">
          <UIcon
            v-if="link.icon"
            :name="link.icon"
            class="relative size-4 shrink-0"
            :class="[
              link.iconClass,
              isActive
                ? 'text-gray-700 dark:text-gray-200'
                : 'text-gray-400 group-hover:text-gray-700 dark:text-gray-500 dark:group-hover:text-gray-200',
            ]"
          />
        </template>
        <template #default="{ link, isActive }">
          <div v-if="isActive" class="absolute inset-y-1.5 left-[-5px] w-[3px] rounded-md bg-blue-600" />
          <span class="relative truncate">
            {{ link.label }}
          </span>
        </template>
      </UVerticalNavigation>
    </div>
  </div>
</template>
